/**
 * Navbar component - Landing page navigation
 */

import {useState, useEffect, useRef} from 'react';
import {Link, useNavigate, useLocation} from 'react-router-dom';
import {cn} from '../../utils/cn';
import {ROUTES} from '../../config/constants';

interface NavItem {
  label: string;
  href: string;
  submenu?: NavItem[];
}

const navItems: NavItem[] = [
  {
    label: 'Product',
    href: `${ROUTES.HOME}#features`,
    submenu: [
      {label: 'Meeting Assistant', href: `${ROUTES.HOME}#features`},
      {label: 'Study Mode', href: ROUTES.STUDY_MODE},
      {label: 'Features', href: `${ROUTES.HOME}#features`},
    ],
  },
  {label: 'Pricing', href: ROUTES.PRICING},
  {label: 'Enterprise', href: ROUTES.ENTERPRISE},
  {
    label: 'Resources',
    href: ROUTES.RESOURCES,
    submenu: [
      {label: 'Docs', href: '/docs'},
      {label: 'Changelog', href: `${ROUTES.BLOG}/changelog`},
      {label: 'Forum', href: ROUTES.HELP_CENTER},
      {label: 'Blog', href: ROUTES.BLOG},
      {label: 'Community', href: ROUTES.HELP_CENTER},
      {label: 'Careers', href: ROUTES.CAREERS},
    ],
  },
];

export function Navbar() {
  const navigate = useNavigate();
  const location = useLocation();
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [hoveredResource, setHoveredResource] = useState(false);
  const [hoveredProduct, setHoveredProduct] = useState(false);
  const resourceRef = useRef<HTMLDivElement>(null);
  const productRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);


  return (
    <nav
      className={cn(
        'fixed top-0 left-0 right-0 z-50 transition-all duration-300',
        {
          'glass border-b border-border dark:bg-[#1E1E1E]/90 dark:backdrop-blur-md': scrolled,
          'bg-transparent dark:bg-transparent': !scrolled,
        }
      )}
    >
      <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-14 sm:h-16">
          {/* Logo */}
          <Link to={ROUTES.HOME} className="flex items-center space-x-2 flex-shrink-0">
            <div className="w-7 h-7 bg-gray-900 rounded-lg flex items-center justify-center">
              <span className="text-white font-bold text-base">V</span>
            </div>
            <span className="font-heading font-bold text-lg text-textPrimary">VoxaPilot</span>
          </Link>

          {/* Desktop Menu - Centered */}
          <div className="hidden md:flex items-center space-x-6 absolute left-1/2 transform -translate-x-1/2">
            {navItems.map((item) => {
              const handleClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
                // If it's an anchor link and we're not on the homepage, navigate first
                if (item.href.includes('#')) {
                  const [path] = item.href.split('#');
                  // If we're not on the target path, navigate to it first
                  if (location.pathname !== path) {
                    e.preventDefault();
                    navigate(item.href);
                  }
                  // If we're already on the path, let the default hash navigation handle it
                }
              };

              if (item.submenu) {
                const isProduct = item.label === 'Product';
                const isHovered = isProduct ? hoveredProduct : hoveredResource;
                const setHovered = isProduct ? setHoveredProduct : setHoveredResource;
                const ref = isProduct ? productRef : resourceRef;
                
                return (
                  <div
                    key={item.label}
                    ref={ref}
                    className="relative"
                    onMouseEnter={() => setHovered(true)}
                    onMouseLeave={() => setHovered(false)}
                  >
                    <button className="text-base font-medium text-textSecondary hover:text-textPrimary transition-colors">
                      {item.label}
                    </button>
                    {isHovered && (
                      <div
                        className="absolute top-full left-0 pt-1 z-50"
                        style={{width: isProduct ? '200px' : '192px'}}
                        onMouseEnter={() => setHovered(true)}
                        onMouseLeave={() => setHovered(false)}
                      >
                        <div className="bg-white dark:bg-[#252526] border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg py-2">
                          <div className={isProduct ? 'flex flex-col' : 'grid grid-cols-2 gap-0'}>
                            {item.submenu.map((subItem) => (
                              <Link
                                key={subItem.label}
                                to={subItem.href}
                                className="px-4 py-2 text-sm text-textSecondary dark:text-[#CCCCCC] hover:bg-gray-50 dark:hover:bg-[#2D2D30] hover:text-textPrimary dark:hover:text-white transition-colors block"
                                onClick={() => {
                                  setHovered(false);
                                  setMobileMenuOpen(false);
                                }}
                              >
                                {subItem.label}
                              </Link>
                            ))}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                );
              }
              
              return (
                <Link
                  key={item.label}
                  to={item.href}
                  onClick={handleClick}
                  className="text-base font-medium text-textSecondary hover:text-textPrimary transition-colors"
                >
                  {item.label}
                </Link>
              );
            })}
          </div>

          {/* CTA Buttons */}
          <div className="hidden md:flex items-center space-x-4">
            <Link to={ROUTES.SIGN_IN}>
              <button className="text-base font-medium text-textSecondary hover:text-textPrimary transition-colors px-4 py-2 border border-gray-200 dark:border-gray-700 rounded-md hover:border-gray-300 dark:hover:border-gray-600">
                Sign In
              </button>
            </Link>
            <button
              onClick={() => {
                // TODO: Replace with actual Windows download URL when available
                const windowsDownloadUrl = 'https://github.com/yourusername/voxapilot/releases/latest/download/VoxaPilot-Setup.exe';
                window.open(windowsDownloadUrl, '_blank');
              }}
              className="text-base font-medium text-white bg-gray-900 dark:bg-orange-600 hover:bg-gray-800 dark:hover:bg-orange-700 transition-colors px-4 py-2 rounded-md flex items-center gap-2"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
              </svg>
              Download
            </button>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="md:hidden p-2"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label="Toggle menu"
          >
            <svg
              className="w-6 h-6 text-textPrimary"
              fill="none"
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              {mobileMenuOpen ? (
                <path d="M6 18L18 6M6 6l12 12" />
              ) : (
                <path d="M4 6h16M4 12h16M4 18h16" />
              )}
            </svg>
          </button>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden py-4 border-t border-border">
            {navItems.map((item) => {
              const handleClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
                // If it's an anchor link and we're not on the homepage, navigate first
                if (item.href.includes('#')) {
                  const [path] = item.href.split('#');
                  // If we're not on the target path, navigate to it first
                  if (location.pathname !== path) {
                    e.preventDefault();
                    navigate(item.href);
                  }
                  // If we're already on the path, let the default hash navigation handle it
                }
                setMobileMenuOpen(false);
              };

              if (item.submenu) {
                return (
                  <div key={item.label} className="py-2">
                    <div className="text-base font-medium text-textSecondary mb-2">{item.label}</div>
                    <div className="pl-4 space-y-1">
                      {item.submenu.map((subItem) => (
                        <Link
                          key={subItem.label}
                          to={subItem.href}
                          onClick={() => setMobileMenuOpen(false)}
                          className="block py-1.5 text-sm text-textSecondary hover:text-textPrimary transition-colors"
                        >
                          {subItem.label}
                        </Link>
                      ))}
                    </div>
                  </div>
                );
              }
              
              return (
                <Link
                  key={item.label}
                  to={item.href}
                  onClick={handleClick}
                  className="block py-2 text-base text-textSecondary hover:text-textPrimary transition-colors"
                >
                  {item.label}
                </Link>
              );
            })}
            <div className="mt-4 pt-4 border-t border-border space-y-2">
              <Link to={ROUTES.SIGN_IN} onClick={() => setMobileMenuOpen(false)}>
                <button className="w-full text-base font-medium text-textSecondary hover:text-textPrimary transition-colors px-4 py-2 border border-gray-200 dark:border-gray-700 rounded-md hover:border-gray-300 dark:hover:border-gray-600">
                  Sign In
                </button>
              </Link>
              <button
                onClick={() => {
                  setMobileMenuOpen(false);
                  // TODO: Replace with actual Windows download URL when available
                  const windowsDownloadUrl = 'https://github.com/yourusername/voxapilot/releases/latest/download/VoxaPilot-Setup.exe';
                  window.open(windowsDownloadUrl, '_blank');
                }}
                className="w-full text-base font-medium text-white bg-gray-900 dark:bg-orange-600 hover:bg-gray-800 dark:hover:bg-orange-700 transition-colors px-4 py-2 rounded-md flex items-center justify-center gap-2"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                </svg>
                Download
              </button>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
}

